import React from 'react';
import {
  Typography,
  Box,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QuickAnswersFAQ from '../concepts/QuickAnswersFAQ';
import CoreConceptsGrid from '../concepts/CoreConceptsGrid';
import AdvancedTopicsAccordion from '../concepts/AdvancedTopicsAccordion';

const ConceptsTab = ({ inputInfo, userInfo, retireYearData, recommendedConversion }) => {
  const conversion = recommendedConversion;
  return (
    <Container maxWidth="lg" sx={{ pt: 0, pb: 4 }}>
      {/* Welcome Section */}
      {/* Quick Answers FAQ Section */}
      <QuickAnswersFAQ />

      {/* Core Concepts Grid Section */}
      <CoreConceptsGrid inputInfo={inputInfo} userInfo={userInfo} retireYearData={retireYearData} />

      {/* Assumptions Accordion Section */}
      <AdvancedTopicsAccordion />

      {/* How You Pay the Conversion Tax Section */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', borderTop: 2, borderColor: 'primary.main', pt: 1.5 }}>
          How You Pay the Conversion Tax - Changes Everything
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
          While some advisors allude to it, the fact that outside-funded conversions double as contributions remains widely misunderstood.
        </Typography>

        <Box component="table" sx={{
          width: '100%',
          maxWidth: '500px',
          borderCollapse: 'collapse',
          mb: 3,
          mx: 'auto',
          '& th, & td': {
            border: '1px solid #ddd',
            padding: '10px 14px',
            textAlign: 'center',
            fontSize: '0.9rem',
          },
          '& th': {
            fontWeight: 'bold',
          },
        }}>
          <thead>
            <tr>
              <th></th>
              <Box component="th" sx={{ backgroundColor: '#fff8e1' }}>Tax-Rate Arbitrage</Box>
              <Box component="th" sx={{ backgroundColor: '#e8f5e9' }}>Capital Contribution</Box>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Inside Funding</strong></td>
              <td>&#10003;</td>
              <td></td>
            </tr>
            <tr>
              <td><strong>Outside Funding</strong></td>
              <td>&#10003;</td>
              <td>&#10003;</td>
            </tr>
          </tbody>
        </Box>

        <Box component="ul" sx={{ textAlign: 'left', pl: 2, m: 0, mt: 1, maxWidth: '700px', mx: 'auto' }}>
          <Typography component="li" variant="body2">Both methods reduce RMDs and earn tax-rate spread profits.</Typography>
          <Typography component="li" variant="body2">Outside funding effectively adds the tax payment to your Roth account.</Typography>
          <Typography component="li" variant="body2">If over age 59 1/2, inside-funding shrinks RMDs and earns rate-spread profits with no out-of-pocket cost, if under 59 1/2, a 10% penalty is incurred ~ AVOID!.</Typography>
        </Box>
      </Box>

      {/* Inside vs Outside Tax Payment Comparison — moved from Recommendation tab 2026-05-04 */}
      {conversion && (
        <Accordion sx={{ mt: 1, mb: 1, '&.Mui-expanded': { mt: 1, mb: 1 } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#e3f2fd', minHeight: 'unset', '&.Mui-expanded': { minHeight: 'unset' }, '& .MuiAccordionSummary-content': { my: 1 }, '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 } }}>
            <Typography variant="subtitle1">
              <strong>COMPARE:</strong> Paying Conversion Tax from <strong>'INSIDE'</strong> the Traditional Account <strong>vs. 'OUTSIDE'</strong> Taxable Savings
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}></TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>INSIDE</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>OUTSIDE</TableCell>
                    <TableCell sx={{ py: 0.5, fontWeight: 'bold', color: 'text.secondary' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ py: 0.5 }}>Traditional Conversion Amount</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>${conversion.conv_amt?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>${conversion.conv_amt?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell sx={{ py: 0.5, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>Traditional balance (and RMDs) reduced equally</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, pl: 3 }}>- Tax Owed (t<sub>D</sub> = {(conversion.conv_dist_tax_rate_tw * 100).toFixed(1)}%)</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>${Math.round(conversion.conv_amt * conversion.conv_dist_tax_rate_tw)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>${Math.round(conversion.conv_amt * conversion.conv_dist_tax_rate_tw)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Conversion Amount - Real Value</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', borderTop: '2px solid #333' }}>${Math.round(conversion.conv_amt - (conversion.conv_amt * conversion.conv_dist_tax_rate_tw))?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', borderTop: '2px solid #333' }}>${Math.round(conversion.conv_amt - (conversion.conv_amt * conversion.conv_dist_tax_rate_tw))?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell sx={{ py: 0.5, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}><strong>Roth Equivalent</strong> = real value of traditional amount converted</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, pl: 3 }}>+ Outside Cash Savings</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>${conversion.conv_tax?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>${conversion.conv_tax?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Pre-Conversion Assets</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', borderTop: '2px solid #333' }}>${Math.round((conversion.conv_amt - (conversion.conv_amt * conversion.conv_dist_tax_rate_tw)) + conversion.conv_tax)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', borderTop: '2px solid #333' }}>${Math.round((conversion.conv_amt - (conversion.conv_amt * conversion.conv_dist_tax_rate_tw)) + conversion.conv_tax)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fffde7' }}>
                    <TableCell sx={{ py: 0.5 }}>Tax-Rate Arbitrage - {(conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate) >= 0 ? 'PROFIT' : 'LOSS'}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, color: (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>${Math.round(conversion.conv_amt * (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate))?.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, color: (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate) >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>${Math.round(conversion.conv_amt * (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate))?.toLocaleString()}</TableCell>
                    <TableCell sx={{ py: 0.5, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>Profit/loss is equal (<strong>Core Roth Conversion</strong>)</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fffde7' }}>
                    <TableCell sx={{ py: 0.5 }}>Outside Tax Payment (t<sub>C</sub> = {(conversion.conv_tax_rate * 100).toFixed(1)}%)</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>—</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>${conversion.conv_tax?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell sx={{ py: 0.5, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>Contribution in disguise (<strong>Synthetic Roth Contribution</strong>)</TableCell>
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fffde7' }}>
                    <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Post-Conversion Roth Value</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', borderTop: '2px solid #333' }}>${Math.round((conversion.conv_amt - (conversion.conv_amt * conversion.conv_dist_tax_rate_tw)) + (conversion.conv_amt * (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate)))?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', borderTop: '2px solid #333' }}>${Math.round((conversion.conv_amt - (conversion.conv_amt * conversion.conv_dist_tax_rate_tw)) + (conversion.conv_amt * (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate)) + conversion.conv_tax)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow sx={{ backgroundColor: '#fffde7' }}>
                    <TableCell sx={{ py: 0.5 }}>Post-Conversion Cash Savings</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>${conversion.conv_tax?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold' }}>—</TableCell>
                    <TableCell sx={{ py: 0.5, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>Inside: tax paid from Traditional leaves savings intact</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, fontWeight: 'bold' }}>Post-Conversion Assets</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', borderTop: '2px solid #333', color: (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate) >= 0 ? 'success.main' : 'error.main' }}>${Math.round((conversion.conv_amt - (conversion.conv_amt * conversion.conv_dist_tax_rate_tw)) + (conversion.conv_amt * (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate)) + conversion.conv_tax)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5, fontWeight: 'bold', borderTop: '2px solid #333', color: (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate) >= 0 ? 'success.main' : 'error.main' }}>${Math.round((conversion.conv_amt - (conversion.conv_amt * conversion.conv_dist_tax_rate_tw)) + (conversion.conv_amt * (conversion.conv_dist_tax_rate_tw - conversion.conv_tax_rate)) + conversion.conv_tax)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell sx={{ py: 0.5, fontStyle: 'italic', color: 'text.secondary', fontSize: '0.8rem' }}>Only tax-rate arbitrage affects immediate post-conversion wealth</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" sx={{ textAlign: 'left', mt: 2, fontStyle: 'italic' }}>
              Paying conversion tax from Inside the Traditional account requires a taxable Traditional distribution that incurs a 10% penalty if under age 59 1/2 - not advised!
              Folks over 59 1/2 that pay with funds Inside their Traditional account capture the full tax-rate spread profit AND also lower their RMDs equally as compared to paying the tax out-of-pocket.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

    </Container>
  );
};

export default ConceptsTab;
